import { Center, Group, Stack } from '@mantine/core';
import { EntityType } from '@repo/common/enums/entityType';
import type { NodeProps } from '@xyflow/react';
import { Handle, Position } from '@xyflow/react';
import { observer } from 'mobx-react-lite';
import { memo, useCallback, useMemo } from 'react';
import EntityNodeChildren from '../EntityNodeChildren/EntityNodeChildren';
import EntityNodeHeader from '../EntityNodeHeader/EntityNodeHeader';
import FetchLineageButton from '../FetchLineageButton/FetchLineageButton';
import { useEntityNode } from './EntityNode.hooks';
import { useStyles } from './EntityNode.styles';
import type { LineageFlowNode } from './EntityNode.types';
import { LineageFlowNodeVisibilityState } from './EntityNode.types';
import EntityNodeSkeleton from './EntityNodeSkeleton';

const EntityNodeContent = observer(
	({ data, selected }: NodeProps<LineageFlowNode>) => {
		const {
			id,
			entity_type,
			direction,
			children_count,
			upstream_count,
			downstream_count,
			upstream_fetched,
			downstream_fetched,
			upstream_collapsed,
			downstream_collapsed,
			source_connectable,
			target_connectable,
		} = data;

		const { createAccess, handleAddTemporaryNode } = useEntityNode(id);

		const isConnectable = useMemo(
			() => source_connectable || target_connectable,
			[source_connectable, target_connectable]
		);
		const hasChildren = useMemo(
			() =>
				children_count !== undefined &&
				children_count > 0 &&
				entity_type === EntityType.table,
			[children_count, entity_type]
		);

		const leftFetchLineageButtonVisible = useMemo(
			() =>
				upstream_count > 0 &&
				(!upstream_fetched || upstream_collapsed) &&
				direction === 'UPSTREAM',
			[upstream_count, upstream_fetched, upstream_collapsed, direction]
		);
		const rightFetchLineageButtonVisible = useMemo(
			() =>
				downstream_count > 0 &&
				(!downstream_fetched || downstream_collapsed) &&
				direction === 'DOWNSTREAM',
			[downstream_count, downstream_fetched, downstream_collapsed, direction]
		);

		const { classes, cx } = useStyles({
			selected: Boolean(selected),
			createAccess,
		});

		const handleLeftClick = useCallback(() => {
			handleAddTemporaryNode(Position.Left);
		}, [handleAddTemporaryNode]);

		const handleRightClick = useCallback(() => {
			handleAddTemporaryNode(Position.Right);
		}, [handleAddTemporaryNode]);

		return (
			<Center className={classes.container}>
				<Group className={classes.wrapper}>
					<FetchLineageButton
						id={id}
						direction={direction}
						count={upstream_count}
						visible={Boolean(leftFetchLineageButtonVisible)}
					/>
					<Stack className={classes.content}>
						<Handle
							id="MAIN_TARGET"
							className={cx(classes.handle, classes.handleLeft)}
							type="target"
							position={Position.Left}
							isConnectable={isConnectable}
							onClick={handleLeftClick}
						/>
						<EntityNodeHeader data={data} selected={selected} />
						{hasChildren && (
							<EntityNodeChildren id={id} count={children_count ?? 0} />
						)}
						<Handle
							id="MAIN_SOURCE"
							className={cx(classes.handle, classes.handleRight)}
							type="source"
							position={Position.Right}
							isConnectable={isConnectable}
							onClick={handleRightClick}
						/>
					</Stack>
					<FetchLineageButton
						id={id}
						direction={direction}
						count={downstream_count}
						visible={Boolean(rightFetchLineageButtonVisible)}
					/>
				</Group>
			</Center>
		);
	}
);

const EntityNode = memo((props: NodeProps<LineageFlowNode>) => {
	const { data } = props;

	if (
		data.visibility === LineageFlowNodeVisibilityState.SKELETON ||
		data.visibility === LineageFlowNodeVisibilityState.LOADING
	) {
		return <EntityNodeSkeleton id={data.id} count={data.children_count ?? 0} />;
	}

	return <EntityNodeContent {...props} />;
});

export default EntityNode;
